"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHistoryRouting = void 0;
const react_1 = require("react");
/**
 * This hook allows routing via the passed in `history` object
 * to push urls to history when major steps of the state machine
 * are activated. This enables forward/back navigation using
 * built-in web browser buttons.
 */
const useHistoryRouting = ({ 
/** optional history object to be passed in from react-router.
 * If null, this hook will do nothing */
history, 
/** current state of the main xstate state machine */
currentState, 
/** callback to send an action to the state machine
 * to 'go back' to a different step */
handleGoBack, 
/** DBW steps configuration. See lib/constants/steps for more */
stepsConfiguration }) => {
    /**
     * The base path that pages will be routed relative to will be
     * set based on the pathname that was in-use when this hook/component
     * was initialized, with trailing slashes removed to avoid double
     * slashes when step paths (which have prefixed slashes) get appended
     */
    const historyBasePathRef = (0, react_1.useRef)(history ? history.location.pathname.replace(/\/$/, '') : null);
    const historyBasePath = historyBasePathRef.current;
    // update url history when curStep is updated
    (0, react_1.useEffect)(() => {
        if (!stepsConfiguration) {
            return;
        }
        const { curStep } = currentState.context;
        if (history && curStep) {
            const queryParams = history.location.search;
            // only transition to states that have explicit steps. There are many
            // states such as loading, init, which don't have their own urls so
            // don't need to get a specific history entry
            const newStepIndex = stepsConfiguration.findIndex(({ stateKey }) => curStep === stateKey);
            if (newStepIndex > -1) {
                const newStep = stepsConfiguration[newStepIndex];
                const oldStepPath = history.location.pathname.replace(historyBasePath, '');
                const oldStepIndex = stepsConfiguration.findIndex(({ path }) => path === oldStepPath);
                const newPath = (historyBasePath + newStep.path).replace(/\/$/, '');
                const newFullPath = newPath + queryParams;
                const currentPath = history.location.pathname.replace(/\/$/, '');
                if (newPath !== currentPath) {
                    if (oldStepIndex > newStepIndex) {
                        // if new path has a lesser index than the current path, that means we're
                        // navigating backwards. Use native 'back' controls to navigate back
                        // as many steps as the difference is. We use this method here instead of
                        // pushing because we don't want the user to be able to go 'forward' again
                        // as that could leave the flow in a bad state.
                        history.go(newStepIndex - oldStepIndex);
                    }
                    else {
                        // otherwise we're going forward in the steps so use a regular
                        // push to update url and enable back/forward nav
                        history.push(newFullPath);
                    }
                }
            }
        }
    }, [currentState, history, historyBasePath, stepsConfiguration]);
    // listen to certain history changes, such as that which happens when the user hits the
    // 'back' button for the previous page, and load the requested step based on url
    (0, react_1.useEffect)(() => {
        const unlisten = history
            ? history.listen((location, action) => {
                if (!stepsConfiguration) {
                    return;
                }
                if (action === 'PUSH') {
                    // ignore pushes because pushes are generated as a side-effect of
                    // state machine changes which already enact changes to current step
                    return;
                }
                // find the state name from the path
                const goToPath = history.location.pathname
                    .replace(historyBasePath, '')
                    .replace(/\/$/, '');
                const goToStep = stepsConfiguration.find(({ path }) => path === goToPath);
                if (goToStep) {
                    const { stateKey } = goToStep;
                    if (stateKey && !currentState.matches(stateKey)) {
                        handleGoBack(stateKey);
                    }
                }
            })
            : null;
        return function cleanup() {
            if (typeof unlisten === 'function') {
                unlisten();
            }
        };
    }, [
        currentState,
        handleGoBack,
        history,
        historyBasePath,
        stepsConfiguration
    ]);
    return null;
};
exports.useHistoryRouting = useHistoryRouting;
//# sourceMappingURL=history-routing.js.map